import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen, ArrowRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const MicroVsSASUvsEURLSimulator = () => {
  const [inputs, setInputs] = useState({
    caAnnuel: '60000',
    charges: '8000',
    parts: '1'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const ca = parseFloat(inputs.caAnnuel) || 0;
    const charges = parseFloat(inputs.charges) || 0;
    const parts = parseFloat(inputs.parts) || 1;
    const benefice = ca - charges;

    // Micro-entreprise BNC
    const micro_cotis = ca * 0.256;
    const micro_abattement = ca * 0.34;
    const micro_imposable = ca - micro_abattement;
    let micro_ir = 0;
    const mq = micro_imposable / parts;
    if (mq > 29579) micro_ir += (Math.min(mq, 84577) - 29579) * 0.30 * parts;
    if (mq > 11600) micro_ir += (Math.min(mq, 29579) - 11600) * 0.11 * parts;
    const micro_total = micro_cotis + Math.max(0, micro_ir);
    const micro_net = ca - micro_total;
    const micro_taux = ca > 0 ? (micro_total / ca * 100) : 0;

    // SASU
    const sasu_salaire = benefice * 0.45;
    const sasu_cp = sasu_salaire * 0.42;
    const sasu_cs = sasu_salaire * 0.22;
    const sasu_net_salaire = sasu_salaire - sasu_cs;
    const sasu_resultat = benefice - sasu_salaire - sasu_cp;
    let sasu_is = 0;
    if (sasu_resultat > 0) {
      sasu_is = Math.min(sasu_resultat, 42500) * 0.15 + Math.max(sasu_resultat - 42500, 0) * 0.25;
    }
    const sasu_dividendes_bruts = Math.max(0, sasu_resultat - sasu_is);
    const sasu_dividendes_nets = sasu_dividendes_bruts * 0.70;
    const sasu_net = sasu_net_salaire + sasu_dividendes_nets;
    const sasu_total_charges = sasu_cp + sasu_cs + sasu_is + sasu_dividendes_bruts * 0.30;
    const sasu_taux = ca > 0 ? (sasu_total_charges / ca * 100) : 0;

    // EURL
    const eurl_remuneration = benefice * 0.50;
    const eurl_cotis = eurl_remuneration * 0.45;
    const eurl_net_remun = eurl_remuneration - eurl_cotis;
    const eurl_resultat = benefice - eurl_remuneration - eurl_cotis;
    let eurl_is = 0;
    if (eurl_resultat > 0) {
      eurl_is = Math.min(eurl_resultat, 42500) * 0.15 + Math.max(eurl_resultat - 42500, 0) * 0.25;
    }
    const eurl_dividendes_bruts = Math.max(0, eurl_resultat - eurl_is);
    const eurl_dividendes_nets = eurl_dividendes_bruts * 0.70;
    const eurl_net = eurl_net_remun + eurl_dividendes_nets;
    const eurl_total_charges = eurl_cotis + eurl_is + eurl_dividendes_bruts * 0.30;
    const eurl_taux = ca > 0 ? (eurl_total_charges / ca * 100) : 0;

    const all = [
      { nom: 'Micro-entreprise', net: Math.round(micro_net), taux: micro_taux.toFixed(1), protection: 'Minimale', comptabilite: 'Très simple', plafond: '77 700€ (services)' },
      { nom: 'SASU', net: Math.round(sasu_net), taux: sasu_taux.toFixed(1), protection: 'Maximale (régime général)', comptabilite: 'Comptabilité complète', plafond: 'Illimité' },
      { nom: 'EURL', net: Math.round(eurl_net), taux: eurl_taux.toFixed(1), protection: 'Bonne (TNS)', comptabilite: 'Comptabilité complète', plafond: 'Illimité' },
    ];
    all.sort((a, b) => b.net - a.net);

    setResults({ comparaison: all, ca, benefice: Math.round(benefice) });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Micro vs SASU vs EURL - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Micro vs SASU vs EURL', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`CA : ${parseInt(inputs.caAnnuel).toLocaleString()} EUR | Charges : ${parseInt(inputs.charges).toLocaleString()} EUR`, 15, y); y += 12;
    if (results.comparaison) {
      results.comparaison.forEach((s, i) => {
        doc.text(`${i + 1}. ${s.nom} : ${s.net?.toLocaleString()} EUR net (taux ${s.taux}%)`, 15, y);
        y += 7;
        doc.setFontSize(9);
        doc.text(`   Protection: ${s.protection} | Comptabilité: ${s.comptabilite}`, 15, y);
        doc.setFontSize(11);
        y += 10;
      });
    }
    y += 5;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, CGI art. 219/197, Code Sécurité Sociale', 15, y);
    doc.save('simulation-micro-vs-sasu-vs-eurl-bozzam.pdf');
  };

  const colors = ['bg-green-50 border-green-300', 'bg-blue-50 border-blue-200', 'bg-gray-50 border-gray-200'];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Paramètres
            </CardTitle>
            <CardDescription>Comparatif complet des 3 statuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>CA annuel (€)</Label>
              <Input data-testid="ca-input" type="text" inputMode="numeric" value={inputs.caAnnuel}
                onChange={(e) => handleInputChange('caAnnuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="60000" className="mt-1" />
            </div>
            <div>
              <Label>Charges annuelles (€)</Label>
              <Input data-testid="charges-input" type="text" inputMode="numeric" value={inputs.charges}
                onChange={(e) => handleInputChange('charges', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="8000" className="mt-1" />
            </div>
            <div>
              <Label>Parts fiscales</Label>
              <Input data-testid="parts-input" type="text" inputMode="decimal" value={inputs.parts}
                onChange={(e) => handleInputChange('parts', e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="1" className="mt-1" />
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-xs text-purple-600">Bénéfice estimé</p>
              <p className="text-lg font-bold text-purple-900">{results.benefice?.toLocaleString()}€</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Micro BNC : 25.6% URSSAF</li>
                    <li>IS : 15% ({'<'}42 500€) / 25%</li>
                    <li>Flat tax : 30%</li>
                    <li>Barème IR 2026</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {results.comparaison?.map((s, i) => (
          <Card key={s.nom} className={`border-2 ${colors[i]}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{s.nom}</CardTitle>
                {i === 0 && <Badge className="bg-green-100 text-green-800">Le + rentable</Badge>}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-4 rounded-lg text-center ${i === 0 ? 'bg-green-100 border-2 border-green-300' : 'bg-white'}`}>
                <p className="text-xs text-gray-600 mb-1">Revenu net annuel</p>
                <p className={`text-2xl font-black ${i === 0 ? 'text-green-900' : 'text-gray-900'}`} data-testid={`result-net-${i}`}>
                  {s.net?.toLocaleString()}€
                </p>
                <p className="text-xs text-gray-500">{Math.round(s.net / 12)?.toLocaleString()}€/mois</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Taux prélèvement</span>
                  <span className="font-semibold">{s.taux}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Protection sociale</span>
                  <span className="font-semibold text-xs">{s.protection}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Comptabilité</span>
                  <span className="font-semibold text-xs">{s.comptabilite}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plafond CA</span>
                  <span className="font-semibold text-xs">{s.plafond}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Comparaison basée sur un CA de <span className="font-bold">{parseInt(inputs.caAnnuel).toLocaleString()}€</span> avec <span className="font-bold">{parseInt(inputs.charges).toLocaleString()}€</span> de charges
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">Sources : URSSAF 2026 | CGI art. 219, 197, 200 A | Code Sécurité Sociale | service-public.fr</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MicroVsSASUvsEURLSimulator;
